"use client";

import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';

type AppearanceContextType = {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
};

export const AppearanceContext = createContext<AppearanceContextType | undefined>(undefined);

export const AppearanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'appearance' } }));
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Listen for other sidebars opening
  React.useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'appearance') {
        setIsOpen(false);
      }
    };

    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, []);

  return (
    <AppearanceContext.Provider value={{ isOpen, toggle, open, close }}>
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (context === undefined) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return context;
};
